import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Modal } from '../ui/Modal';
import TruncatedText from '../ui/TruncatedText';
import { withArchiveExt } from '../../utils/archiveCommand';
import { errText, joinLocal } from './ftpViewHelpers';

/* ── Export local files as an archive ──────────────────────────────────── */

export const EXPORT_FORMATS = [
  { id: 'zip', ext: '.zip' },
  { id: 'tar.gz', ext: '.tar.gz' },
];

/**
 * Export the selected local files and folders as ONE archive — asking what
 * format, what name, and WHERE, the way the owner described.
 *
 * The folder defaults to the one the user is looking at, with Browse for any
 * other. Nothing about the result is decided silently: the format is the radio,
 * the name is theirs, and an archive that already exists is refused (Rust
 * checks it, and again just before the final rename) rather than replaced.
 */
export function ExportArchiveModal({ sources, defaultFolder, onClose, onDone }) {
  const { t } = useTranslation();
  const [format, setFormat] = useState('zip');
  const ext = EXPORT_FORMATS.find((f) => f.id === format).ext;
  const [name, setName] = useState(
    withArchiveExt(sources.length === 1 ? sources[0].name : 'archive', '.zip')
  );
  const [folder, setFolder] = useState(defaultFolder);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const pickFormat = (id) => {
    setFormat(id);
    setName((n) => withArchiveExt(n, EXPORT_FORMATS.find((f) => f.id === id).ext));
  };

  const browse = async () => {
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const picked = await open({ directory: true, multiple: false, defaultPath: folder });
      const chosen = Array.isArray(picked) ? picked[0] : picked;
      if (chosen) setFolder(String(chosen));
    } catch (err) {
      setError(errText(err));
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const finalName = withArchiveExt(name.trim(), ext);
    // A name that is ONLY an extension ("" or ".zip") would export as a hidden
    // file literally called `.zip`. withArchiveExt adds the extension back to
    // an empty stem, so the stem is what has to be checked.
    if (busy || !withArchiveExt(name.trim(), '').trim()) return;
    if (/[\\/]/.test(finalName.replace(/\.(zip|tar\.gz)$/i, ''))) {
      setError(t('ftp.archiveBadName'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await invoke('ftp_local_export', {
        sources: sources.map((x) => x.path),
        dest: joinLocal(folder, finalName),
      });
      onDone({ ...res, name: finalName, folder });
    } catch (err) {
      const text = errText(err);
      setError(text.startsWith('dest_exists') ? t('ftp.nameExists', { name: finalName }) : text);
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title={t('ftp.exportTitle')}>
      <form onSubmit={submit} className="space-y-3">
        <p className="ftp-delete-question">
          {sources.length === 1
            ? t('ftp.exportOne', { name: sources[0].name })
            : t('ftp.exportMany', { count: sources.length })}
        </p>
        <div className="ftp-export-formats" role="radiogroup" aria-label={t('ftp.exportFormat')}>
          {EXPORT_FORMATS.map((f) => (
            <label key={f.id} className="ftp-export-format">
              <input
                type="radio"
                name="ftp-export-format"
                className="checkbox-brand cursor-pointer"
                checked={format === f.id}
                disabled={busy}
                onChange={() => pickFormat(f.id)}
              />
              <span>{f.ext}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="modal-logo-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
            {t('ftp.archiveName')}
          </label>
          <input
            type="text"
            className="modal-input"
            style={{ width: '100%' }}
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            spellCheck={false}
            autoFocus
          />
        </div>
        <div>
          <label className="modal-logo-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
            {t('ftp.exportFolder')}
          </label>
          <div className="ftp-export-folder">
            <TruncatedText className="ftp-export-folder-path" text={folder} />
            <button type="button" className="ftp-mini-btn" onClick={browse} disabled={busy}>
              {t('ftp.browse')}
            </button>
          </div>
        </div>
        {error && <div className="ftp-modal-error">{error}</div>}
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!name.trim() || busy}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {busy ? t('ftp.exporting') : t('ftp.export')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
